import React, { useEffect, useState } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  Grid,
  Typography,
  TablePagination,
} from "@mui/material";
import MoreMenu from "../Admin-PaymentMethod/MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import {
  getPaymentBrands,
  paymentBrandAction,
  getActivePaymentMethods,
  getPaymentBrandFilterConditions,
} from "../../services/payment";
import { MultiSelect } from "../table/Filter.js";
import { Log } from "../../services/services.js";
import * as Yup from "yup";
import { useFormik } from "formik";
import { StyledTableRow } from "../TableRow.js";
import FormSearch from "../FormSearch.js";
import CircularLoader from "../circularLoader.js";
import FormDialog from "../FormDialog.js";
import Label from "../Label.js";
import { GREY } from "../../theme/palette.js";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "name", label: "Payment Brands" },
  { id: "payment_method_list", label: "Payment Methods" },
  { id: "is_active", label: "Active" },
  { id: ""}
  // { id: "updated", label: "Updated At", align: "center" },
];

function PaymentBrands({ userRole }) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);
  const [paymentBrands, setPaymentBrands] = useState({ results: [], count: 0 });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [currentFilter, setCurrentFilter] = useState(defaultValue);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    selectedId && setSelectedId();
  };

  const DeleteBrandDialog = ({ open, selectedId, setResponse, setLoaded }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: Yup.object().shape({
        reason: Yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Delete Payment Brand by Id: ${selectedId}`)
        await paymentBrandAction(
          "REMOVE",
          { pk: selectedId, action_message: values.reason },
          setResponse,
          setLoaded
        );
        handleClose();
      }
    });

    const handleClose = () => {
      setOpenDeleteDialog(false);
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title="Delete this payment brand?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const EditBrandDialog = ({ open, selectedId, setResponse, setLoaded, onClose }) => {
    let brandToEdit = (paymentBrands.results || []).find((e) => e.id === selectedId);

    useEffect(() => {
      const paymentMethodIds = [];
      brandToEdit.payment_method.forEach((method) =>
        paymentMethodIds.push(method.id)
      );
      setFieldValue("name", brandToEdit.name || '')
      setFieldValue("payment_method", paymentMethodIds || [])
    }, []);

    const {
      handleChange,
      handleSubmit,
      handleBlur,
      errors,
      values,
      touched,
      isSubmitting,
      setFieldValue,
    } = useFormik({
      initialValues: {
        name: "",
        payment_method: [],
      },
      validationSchema: Yup.object().shape({
        name: Yup.string()
          .required("This field is required")
          .test('not-whitespace-only', 'This field is required', function(value) {
            return value && value.trim().length > 0;
          }),
        payment_method: Yup.array().min(1, "This field is required"),
      }),
      onSubmit: async (values, { setSubmitting }) => {
        const name = values.name.trim();
        setSubmitting(true);
        Log.info(`Click button [Submit] - Update Payment Brand details by Id: ${selectedId}`, { ...values, name })
        await paymentBrandAction(
          "UPDATE",
          { pk: selectedId, ...values, name },
          setResponse,
          setLoaded
        );
        setOpenEditDialog(false);
        setSubmitting(false);
      },
    });

    return (
      <FormDialog
        open={open}
        onClose={onClose}
        title="Edit this payment method?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Typography sx={{ pb: 0.5, color: GREY[600], fontSize: 12 }}>
          Payment Methods
        </Typography>
        <MultiSelect
          name="payment_method"
          options={paymentMethods.name.map((method) => ({
            value: method.id ? method.id : "",
            label: method.name ? method.name : "",
          }))}
          selectedValues={values.payment_method}
          setSelectedValues={data => setFieldValue("payment_method", data)}
          errorMsg={touched.payment_method && errors.payment_method}
        />
        <TextField
          name="name"
          label="Name of payment brand"
          fullWidth
          variant="standard"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          disabled={isSubmitting}
          sx={{ my: 2 }}
        />
      </FormDialog>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPaymentBrands({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...currentFilter, ...queryParams },
    });
    setPagination({
      page: queryParams.page,
      page_size: queryParams.page_size,
    });
  };

  const filteredList = applySortFilter(
    paymentBrands.results || [],
    order,
    orderBy
  );

  const isItemNotFound = (paymentBrands.results || []).length === 0;

  const renderData = data => {
    if (!data || !data.results) return;
    const newData = data.results.map(item => ({
      ...item,
      payment_method_list: item.payment_method
      ?.map((method) => method.name)
      .join(", ")
    }))
    setPaymentBrands({ count: data.count, results: newData });
  }

  useEffect(() => {
    getPaymentBrands({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...defaultValue, ...pagination },
    });
    getActivePaymentMethods(setPaymentMethods, setLoaded);
    getPaymentBrandFilterConditions(setBrandFilter, setFLoaded);
  }, []);

  // filter
  const [brandFilter, setBrandFilter] = useState("");
  const [fLoaded, setFLoaded] = useState(false);
  const onFilterSubmit = (fParams) => {
    const newParams = {
      ...fParams,
      name: fParams.name?.label,
      payment_method: fParams.payment_method?.id,
      name_input: inputValues?.name?.trim() || null,
      payment_method_input: inputValues?.payment_method?.trim() || null,
    }
    if (newParams.is_active === 'all') delete newParams.is_active;
    setCurrentFilter(newParams);
    setLoaded(false);
    queryPage({ ...newParams, page: 1, page_size: pagination.page_size });
  };

  const schema = [
    {
      type: "search-select",
      label: "Payment Brand",
      param: "name",
      options: brandFilter?.name?.map((brand) => ({
        label: brand.name,
        id: brand.id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Payment Method",
      param: "payment_method",
      options: brandFilter?.payment_method?.map((method) => ({
        label: method.name,
        id: method.id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map(({ id, name, payment_method_list, is_active }) => {
                      return (
                        <StyledTableRow
                          hover
                          key={id}
                          sx={{ cursor: "default" }}
                        >
                          <TableCell children={name} id={id} />
                          <TableCell
                            children={payment_method_list}
                            id={id}
                          />
                          <TableCell
                            children={
                              <Label color={is_active ? "success" : "error"}>
                                {is_active ? "YES" : "NO"}
                              </Label>
                            }
                            id={id}
                          />
                          <TableCell align="right">
                            {is_active && <MoreMenu
                              setOpenDeleteDialog={setOpenDeleteDialog}
                              setOpenEditDialog={setOpenEditDialog}
                              setRow={() => setSelectedId(id)}
                              userRole={userRole}
                            />}
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={paymentBrands?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditBrandDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteBrandDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default PaymentBrands;
